/**
 * =============================================================================
 *  BL-custom.js
 *  Custom JavaScript overrides for the Hyper Theme (Belari Design Store)
 * =============================================================================
 *
 *  HOW TO INCLUDE THIS FILE
 *  ------------------------
 *  Add this line inside <head> or before </body> in layout/theme.liquid:
 *
 *    {{ 'BL-custom.js' | asset_url | script_tag }}
 *
 *  Or using the defer pattern (recommended):
 *    <script src="{{ 'BL-custom.js' | asset_url }}" defer></script>
 *
 * =============================================================================
 */


/* =============================================================================
   SECTION 01 — TOLSTOY: HIDE BLOCK WHEN NO STORY FOUND
   =============================================================================
   The Tolstoy app sets data-status="notFound" on <tolstoy-stories> when no
   video story exists for the current product. It does this asynchronously
   after its API responds — so CSS :has() alone can miss the change.

   This script uses a MutationObserver to watch for that attribute change
   and adds the class "bl-tolstoy-hidden" to the outer .product__block wrapper,
   which BL-custom.css then hides with display:none.

   It also runs an immediate check on page load to catch cases where the
   attribute is already set before the observer starts.
   ============================================================================= */

(function () {
  'use strict';

  /**
   * Given a <tolstoy-stories> element, find its outer .product__block
   * ancestor and show or hide it based on data-status.
   */
  function updateTolstoyBlock(el) {
    var status = el.getAttribute('data-status');
    var block = el.closest('.product__block');
    if (!block) return;

    if (status === 'notFound') {
      block.classList.add('bl-tolstoy-hidden');
    } else {
      block.classList.remove('bl-tolstoy-hidden');
    }
  }

  /**
   * SECTION 02 — KONVO (WHATSAPP): SHADOW DOM POSITION FIX
   * The Konvo widget uses Shadow DOM, isolating it from global CSS.
   * We inject a <style> block directly into its shadow root.
   */
  function applyKonvoShadowStyle(el) {
    if (!el) return;
    if (!el.shadowRoot) {
      if (el.__blKonvoRetryScheduled) return;
      el.__blKonvoRetryScheduled = true;
      setTimeout(function () {
        el.__blKonvoRetryScheduled = false;
        applyKonvoShadowStyle(el);
      }, 300);
      return;
    }
    var root = el.shadowRoot;

    if (!root.querySelector('.bl-konvo-position-fix')) {
      var style = document.createElement('style');
      style.className = 'bl-konvo-position-fix';
      style.textContent = '.ui-widget-wrapper--position-bottom-right, .ui-widget-wrapper { bottom: 40px !important; transform: translate(-2px, -40px) !important; }';
      root.appendChild(style);
    }

    function enforceKonvoBottom() {
      var wrapper = root.querySelector('.ui-widget-wrapper--position-bottom-right, .ui-widget-wrapper');
      if (!wrapper) return false;

      wrapper.style.setProperty('bottom', '33px', 'important');
      wrapper.style.setProperty('transform', 'translate(-2px, -33px)', 'important');
      return true;
    }

    enforceKonvoBottom();

    if (el.__blKonvoObserverInstalled) return;
    el.__blKonvoObserverInstalled = true;

    var shadowObserver = new MutationObserver(function () {
      enforceKonvoBottom();
    });

    shadowObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }

  /**
   * Attach a MutationObserver to a single <tolstoy-stories> element
   * so we react the moment data-status changes.
   */
  function observeTolstoyElement(el) {
    var observer = new MutationObserver(function () {
      updateTolstoyBlock(el);
    });
    observer.observe(el, {
      attributes: true,
      attributeFilter: ['data-status']
    });
    // Also check immediately in case the status is already set
    updateTolstoyBlock(el);
  }

  /**
   * Find and initialize all relevant elements.
   */
  function initCustomFixes() {
    document.querySelectorAll('tolstoy-stories').forEach(observeTolstoyElement);
    document.querySelectorAll('konvo-widget').forEach(applyKonvoShadowStyle);
  }

  /**
   * Watch for new elements added to the DOM dynamically.
   */
  var domObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return; // elements only

        var tagName = node.tagName ? node.tagName.toLowerCase() : '';

        // Handle Tolstoy
        if (tagName === 'tolstoy-stories') {
          observeTolstoyElement(node);
        }
        node.querySelectorAll && node.querySelectorAll('tolstoy-stories').forEach(observeTolstoyElement);

        // Handle Konvo
        if (tagName === 'konvo-widget') {
          applyKonvoShadowStyle(node);
        }
        node.querySelectorAll && node.querySelectorAll('konvo-widget').forEach(applyKonvoShadowStyle);
      });
    });
  });

  // Start watching the whole document for new elements
  domObserver.observe(document.body, { childList: true, subtree: true });

  // Run the initial check
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomFixes);
  } else {
    initCustomFixes();
  }

})();

(function () {
  'use strict';

  function enableHeaderTransitions() {
    if (!document.body) return;

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.body.classList.add('bl-header-transition-ready');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableHeaderTransitions, { once: true });
  } else {
    enableHeaderTransitions();
  }
})();

(function () {
  'use strict';

  var BODY_CLASS = 'bl-pdp-header-force-transparent';
  var TRANSPARENT_ZONE_RATIO = 1 / 3;
  var TRANSPARENT_ZONE_HYSTERESIS = 24;
  var mobileQuery = window.matchMedia('(max-width: 767.98px)');
  var currentMediaWrapper = null;
  var resizeObserver = null;
  var rafId = null;
  var lastScrollTop = window.scrollY || window.pageYOffset || 0;
  var hasExitedTransparentZone = false;
  var transparentZoneBaseTop = null;
  var transparentZoneState = null;

  function bindMediaQuery(listener) {
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', listener);
      return;
    }

    mobileQuery.addListener(listener);
  }

  function isEligible() {
    return document.body.classList.contains('product-template')
      && mobileQuery.matches
      && !!document.querySelector('header[is="sticky-header"][data-enable-transparent="true"]');
  }

  function observeMediaWrapper(wrapper) {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    if (!wrapper || !window.ResizeObserver) return;

    resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(wrapper);
  }

  function getMediaWrapper() {
    var nextWrapper = document.querySelector('.product__media-wrapper');

    if (nextWrapper !== currentMediaWrapper) {
      currentMediaWrapper = nextWrapper;
      transparentZoneBaseTop = null;
      transparentZoneState = null;
      observeMediaWrapper(currentMediaWrapper);
    }

    return currentMediaWrapper;
  }

  function getElementDocumentTop(element) {
    var top = 0;
    var current = element;

    while (current) {
      top += current.offsetTop || 0;
      current = current.offsetParent;
    }

    return top;
  }

  function syncTransparentZoneBaseTop(force) {
    var mediaWrapper = getMediaWrapper();
    if (!mediaWrapper) {
      transparentZoneBaseTop = null;
      return;
    }

    var documentTop = getElementDocumentTop(mediaWrapper);
    var body = document.body;
    var isInitialTransparentState = !body.classList.contains('header-pinned') && !body.classList.contains('is-hide-nav');
    var isForcedTransparentState = body.classList.contains(BODY_CLASS);

    if (force || transparentZoneBaseTop === null || isInitialTransparentState || isForcedTransparentState) {
      transparentZoneBaseTop = documentTop;
    }
  }

  function getTransparentZoneEnd() {
    var mediaWrapper = getMediaWrapper();
    if (!mediaWrapper) return 0;

    if (transparentZoneBaseTop === null) {
      syncTransparentZoneBaseTop(true);
    }

    var mediaHeight = mediaWrapper.offsetHeight || 0;
    if (!mediaHeight || transparentZoneBaseTop === null) return 0;

    return transparentZoneBaseTop + mediaHeight * TRANSPARENT_ZONE_RATIO;
  }

  function isInsideTransparentZone(scrollTop, zoneEnd) {
    if (!zoneEnd) {
      transparentZoneState = false;
      return false;
    }

    var enterThreshold = zoneEnd - TRANSPARENT_ZONE_HYSTERESIS;
    var exitThreshold = zoneEnd + TRANSPARENT_ZONE_HYSTERESIS;

    if (transparentZoneState === null) {
      transparentZoneState = scrollTop <= zoneEnd;
      return transparentZoneState;
    }

    if (transparentZoneState) {
      if (scrollTop >= exitThreshold) {
        transparentZoneState = false;
      }
    } else if (scrollTop <= enterThreshold) {
      transparentZoneState = true;
    }

    return transparentZoneState;
  }

  function updateTransparentStickyState() {
    rafId = null;
    var scrollTop = window.scrollY || window.pageYOffset || 0;

    if (!isEligible()) {
      document.body.classList.remove(BODY_CLASS);
      hasExitedTransparentZone = false;
      transparentZoneState = null;
      lastScrollTop = scrollTop;
      return;
    }

    var transparentZoneEnd = getTransparentZoneEnd();
    var isInsideZone = isInsideTransparentZone(scrollTop, transparentZoneEnd);
    var isPinned = document.body.classList.contains('header-pinned');
    var isScrollingDown = scrollTop > lastScrollTop;
    var shouldStayTransparent = false;

    if (isInsideZone) {
      hasExitedTransparentZone = false;
      shouldStayTransparent = true;
    } else if (!isPinned) {
      hasExitedTransparentZone = true;
    }

    if (!shouldStayTransparent && !hasExitedTransparentZone && isPinned && isScrollingDown) {
      // Keep the transparent look until the native sticky header actually hides on scroll down.
      shouldStayTransparent = true;
    }

    document.body.classList.toggle(BODY_CLASS, shouldStayTransparent);
    lastScrollTop = scrollTop;
  }

  function scheduleUpdate() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(updateTransparentStickyState);
  }

  function refreshTransparentStickyState() {
    getMediaWrapper();
    syncTransparentZoneBaseTop(true);
    scheduleUpdate();
  }

  bindMediaQuery(refreshTransparentStickyState);
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', refreshTransparentStickyState);
  document.addEventListener('variant:changed', refreshTransparentStickyState);
  document.addEventListener('bl:grouping:swapped', refreshTransparentStickyState);
  document.addEventListener('shopify:section:load', refreshTransparentStickyState);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshTransparentStickyState);
  } else {
    refreshTransparentStickyState();
  }
})();
